import { capitalize } from 'lodash';
import { BrowserPurpose, BrowserId } from './types';
import { v4 as uuid } from 'uuid';

export async function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function toTitleCase(string: string | null) {
  return string
    ?.split(' ')
    .map((word) => capitalize(word))
    .join(' ');
}

/** A timer that runs an actions when the counter reaches 0. You can refresh the timer easily */
export class RefreshTimer {
  private timer: NodeJS.Timeout | null = null;

  constructor(private action: () => void, private time: number) {
    this.refresh();
  }

  /** Restart the timer */
  refresh() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.action();
    }, this.time) as any;
  }

  clear() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }
}

/** Handles a set of refresh timers keyed by some id */
export class RefreshTimerSet {
  /** Maps a clientId to their inactivity timer */
  private readonly refreshTimers = new Map<string, RefreshTimer>();

  constructor(private action: (key: string) => void, private time: number) {}

  /** Returns the list of all active timers */
  list() {
    return Array.from(this.refreshTimers.keys());
  }

  /** Refresh the timer by key. Creates the timer if it doesn't exist */
  refresh(key: string) {
    const timer = this.refreshTimers.get(key);

    if (timer) {
      timer.refresh();
    } else {
      this.add(key);
    }
  }

  add(key: string) {
    const newTimer = new RefreshTimer(() => this.action(key), this.time);
    this.refreshTimers.set(key, newTimer);
  }

  /** Remove the timer by key */
  delete(key: string) {
    this.refreshTimers.get(key)?.clear();
    this.refreshTimers.delete(key);
  }
}

/** Debounce a request and abort it if the request is in progress */
export class Debouncer<T> {
  private timeout: NodeJS.Timeout | null = null;
  private abortController = new AbortController();

  constructor(
    private action: (val: T, signal: AbortSignal) => void,
    private debounceTime: number,
  ) {}

  /** Emit a value to the action. If we emmited again before the debounce time expires then the last emit is cancelled and the new one is queued  */
  emit(value: T) {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    this.timeout = setTimeout(() => {
      this.action(value, this.abortController.signal);
    }, this.debounceTime) as any;
  }
}

/** Used for timing multiple events in succession */
export class StopWatch {
  private currentTime = Date.now();

  lap(message: string) {
    const now = Date.now();
    console.log(message + ': ' + (now - this.currentTime) + 'ms');
    this.currentTime = now;
  }
}

export function getPageId(
  submissionId: string,
  purpose: BrowserPurpose,
): BrowserId {
  return `${purpose}-${submissionId}`;
}

export function createUUID() {
  return uuid();
}

export function isNotNullOrUndefined<T>(
  value: T | null | undefined,
): value is T {
  return value != null;
}
